/**
 * ScheduleService - Web Player için Playlist Zamanlama Yönetimi
 * Desktop veeq-player ScheduleService'inden adapt edilmiştir
 *
 * Temel Fark: Dosya indirme yerine CDN stream kullanır
 * localStorage ile persistence sağlar
 */

import { Schedule, SchedulesData, Playlist } from '@/types/schedule';
import { localStateStore } from '@/utils/localStateStore';

class ScheduleService {
  private static instance: ScheduleService;

  private schedules: Map<string, Schedule> = new Map();
  private activeSchedule: Schedule | null = null;
  private checkInterval: number | null = null;

  private readonly SCHEDULES_STORAGE_KEY = 'schedules';
  private readonly CHECK_INTERVAL = 60000; // 60 saniye

  private constructor() {
    console.log('[ScheduleService] Initializing...');

    // Kayıtlı schedule'ları yükle
    this.loadSchedules();

    // Aktif schedule varsa, hemen kontrol et ve restore et (Browser refresh fix)
    setTimeout(() => {
      this.checkSchedules();
    }, 100);

    // Periyodik kontrol başlat
    this.startPeriodicCheck();
  }

  /**
   * Singleton instance
   */
  static getInstance(): ScheduleService {
    if (!ScheduleService.instance) {
      ScheduleService.instance = new ScheduleService();
    }
    return ScheduleService.instance;
  }

  /**
   * localStorage'dan schedule'ları yükler
   */
  private loadSchedules(): void {
    try {
      const parsed = localStateStore.getJson<SchedulesData>(this.SCHEDULES_STORAGE_KEY, null);

      if (parsed) {
        if (parsed.schedules && Array.isArray(parsed.schedules)) {
          parsed.schedules.forEach(schedule => {
            const scheduleId = schedule._id || schedule.id;
            if (scheduleId) {
              this.schedules.set(scheduleId, schedule);
            }
          });
        }

        // Aktif schedule bilgisini yükle
        if (parsed.activeScheduleId) {
          this.activeSchedule = this.schedules.get(parsed.activeScheduleId) || null;
        }
      }
    } catch (error) {
      console.error('[ScheduleService] Schedule yükleme hatası:', error);
    }
  }

  /**
   * Schedule'ları localStorage'a kaydeder
   */
  private saveSchedulesToStorage(): void {
    try {
      const data: SchedulesData = {
        schedules: Array.from(this.schedules.values()),
        activeScheduleId: this.activeSchedule ? (this.activeSchedule._id || this.activeSchedule.id || null) : null,
        lastUpdated: new Date().toISOString()
      };

      localStateStore.setJson(this.SCHEDULES_STORAGE_KEY, data);
    } catch (error) {
      console.error('[ScheduleService] Schedule kaydetme hatası:', error);
    }
  }

  /**
   * Yeni bir schedule kaydeder veya mevcut olanı günceller
   * Desktop'taki saveSchedule() metodunun web adaptasyonu
   */
  async saveSchedule(scheduleData: Schedule): Promise<void> {
    try {
      if (!scheduleData) {
        console.error('[ScheduleService] Schedule verisi boş!');
        return;
      }

      const scheduleId = scheduleData._id || scheduleData.id;
      if (!scheduleId) {
        console.error('[ScheduleService] Schedule ID bulunamadı!');
        return;
      }

      // Update mi, yeni schedule mı?
      const existingSchedule = this.schedules.get(scheduleId);
      const isUpdate = !!existingSchedule;

      // Aktif schedule güncelleniyor mu kontrol et (Desktop mantığı)
      const activeScheduleId = this.activeSchedule ?
        (this.activeSchedule._id || this.activeSchedule.id) : null;
      const isActiveScheduleUpdate = isUpdate && activeScheduleId === scheduleId;

      // Eski playlist ID'yi al (Map'e yazmadan önce)
      const oldPlaylistId = existingSchedule?.playlist ? (existingSchedule.playlist._id || (existingSchedule.playlist as any).id) : null;
      const newPlaylistId = scheduleData.playlist ? (scheduleData.playlist._id || (scheduleData.playlist as any).id) : null;
      const isActiveNow = this.isScheduleActive(scheduleData, new Date());

      // Schedule'ı Map'e kaydet
      this.schedules.set(scheduleId, scheduleData);

      // localStorage'a kaydet
      this.saveSchedulesToStorage();

      if (!isUpdate && isActiveNow) {
        // Canli push ile gelen aktif schedule, eski local siraya takilmadan hemen devreye girmeli.
        console.log('[ScheduleService] Yeni aktif schedule direkt devreye aliniyor:', scheduleId);
        this.activeSchedule = scheduleData;
        await this.activateSchedulePlaylist(scheduleData);
        this.saveSchedulesToStorage();
      } else if (isActiveScheduleUpdate) {
        // Aktif schedule güncelleniyor - Desktop gibi yeniden aktive et
        console.log('[ScheduleService] Aktif schedule güncelleniyor:', scheduleId);

        // Playlist aynıysa sadece schedule verisini güncelle, yeniden aktive etme
        if (oldPlaylistId && oldPlaylistId === newPlaylistId) {
          console.log('[ScheduleService] Playlist aynı, sadece schedule verisi güncellendi:', scheduleId);
          this.activeSchedule = scheduleData;
          this.saveSchedulesToStorage();
          return;
        }

        // Backend schedule-updated gönderdiyse, schedule geçerlidir - direkt aktive et
        // Zaman kontrolü yapmaya gerek yok, backend zaten kontrol etti
        console.log('[ScheduleService] Playlist DEĞİŞTİ, yeniden aktive ediliyor:', scheduleId);
        this.activeSchedule = scheduleData;
        await this.activateSchedulePlaylist(scheduleData);
      } else {
        // Normal akış - yeni schedule veya aktif olmayan schedule güncellendi
        await this.checkSchedules();
      }

      // Custom event dispatch et
      window.dispatchEvent(new CustomEvent('schedule-saved', {
        detail: scheduleData
      }));
    } catch (error) {
      console.error('[ScheduleService] Schedule kaydetme hatası:', error);
    }
  }

  /**
   * Bir schedule'ı siler
   * Zamanlanmış playlist de temizlenir, manuel playlist devreye girer
   */
  async deleteSchedule(scheduleId: string): Promise<void> {
    try {
      if (!scheduleId) {
        console.error('[ScheduleService] Silinecek schedule ID boş!');
        return;
      }

      // Schedule'ı bul
      const schedule = this.schedules.get(scheduleId);

      // Zamanlanmış playlist'i localStorage'dan temizle
      const scheduledPlaylistId = this.getScheduledPlaylistId(schedule);

      // Eğer aktif schedule siliniyorsa, aktif schedule'ı temizle
      const wasActive = this.activeSchedule &&
        (this.activeSchedule._id === scheduleId || this.activeSchedule.id === scheduleId);

      if (wasActive) {
        this.activeSchedule = null;
      }

      // Schedule'ı Map'ten sil
      this.schedules.delete(scheduleId);

      // localStorage'a kaydet
      this.saveSchedulesToStorage();

      // Manuel playlist'e SADECE aktif schedule silinmişse geri dön
      // Gelecek zamanlı (inactive) schedule silindiğinde mevcut çalmayı kesme
      if (wasActive) {
        console.log('[ScheduleService] Aktif schedule silindi, manuel playlist\'e dönülüyor');
        await this.switchToManualPlaylist();
      } else {
        await this.checkSchedules();

        // Sadece silinen schedule'a ait playlist halen çalıyorsa ve başka aktif schedule yoksa manuel playliste dön.
        // Aynı playlistle yeni schedule devreye girdiyse activeSchedule dolu kalır ve müzik kesilmez.
        try {
          const parsed = this.getCurrentPlaylistFromStorage();
          const replacementSchedule = this.findActiveScheduleUsingPlaylist(scheduledPlaylistId, scheduleId);

          if (!replacementSchedule && !this.activeSchedule && scheduledPlaylistId && parsed?._id === scheduledPlaylistId) {
            console.log('[ScheduleService] Silinen schedule playlisti hala aktif, manuel playlist\'e dönülüyor');
            await this.switchToManualPlaylist();
            return;
          }

          if (!replacementSchedule && scheduledPlaylistId && parsed?._id !== scheduledPlaylistId) {
            localStateStore.remove(`playlist_${scheduledPlaylistId}`);
          }
        } catch (e) { /* ignore */ }
        console.log('[ScheduleService] Gelecek zamanlı schedule silindi, mevcut çalma devam ediyor');
      }

      // Custom event dispatch et
      window.dispatchEvent(new CustomEvent('schedule-deleted', {
        detail: { scheduleId }
      }));
    } catch (error) {
      console.error('[ScheduleService] Schedule silme hatası:', error);
    }
  }

  /**
   * Periyodik schedule kontrolü başlatır
   * Her dakika çalışır (Desktop gibi)
   */
  private startPeriodicCheck(): void {
    // Önceki interval varsa temizle
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    // Her dakika kontrol et
    this.checkInterval = window.setInterval(() => {
      this.checkSchedules();
    }, this.CHECK_INTERVAL);
  }

  /**
   * Periyodik kontrolü durdurur
   */
  stopPeriodicCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Tüm schedule'ları kontrol eder ve aktif olanı belirler
   * Desktop'taki checkSchedules() metodunun aynısı
   */
  async checkSchedules(): Promise<void> {
    try {
      const now = new Date();
      let foundActiveSchedule: Schedule | null = null;

      // Tüm schedule'ları kontrol et
      for (const schedule of this.schedules.values()) {
        if (this.isScheduleActive(schedule, now)) {
          foundActiveSchedule = schedule;
          break;
        }
      }

      // Basit karşılaştırma - sadece ID kontrolü
      const previousActiveId = this.activeSchedule ? (this.activeSchedule._id || this.activeSchedule.id) : null;
      const newActiveId = foundActiveSchedule ? (foundActiveSchedule._id || foundActiveSchedule.id) : null;

      if (previousActiveId !== newActiveId) {
        this.activeSchedule = foundActiveSchedule;

        if (foundActiveSchedule) {
          await this.activateSchedulePlaylist(foundActiveSchedule);
        } else {
          await this.switchToManualPlaylist();
        }

        this.saveSchedulesToStorage();
      }
    } catch (error) {
      console.error('[ScheduleService] Schedule kontrol hatası:', error);
    }
  }

  /**
   * Bir schedule'ın şu an aktif olup olmadığını kontrol eder
   * Desktop'taki isScheduleActive() metodunun aynısı
   */
  private isScheduleActive(schedule: Schedule, now: Date): boolean {
    try {
      if (!schedule) return false;

      const startDate = new Date(schedule.startDate);
      const endDate = new Date(schedule.endDate);

      // Tarih aralığı kontrolü
      if (now < startDate || now > endDate) {
        return false;
      }

      // Split yaklaşımı: Exception kontrolü kaldırıldı
      // Her schedule kendi tarih aralığını temsil eder

      // repeatType'a göre kontrol
      const repeatType = schedule.repeatType || schedule.recurrence?.type || 'once';

      switch (repeatType) {
        case 'once':
          return this.checkOnceSchedule(schedule, now);

        case 'daily':
          return this.checkDailySchedule(schedule, now);

        case 'weekly':
          return this.checkWeeklySchedule(schedule, now);

        case 'monthly':
          return this.checkMonthlySchedule(schedule, now);

        default:
          return this.checkOnceSchedule(schedule, now);
      }
    } catch (error) {
      console.error('[ScheduleService] Schedule aktiflik kontrolü hatası:', error);
      return false;
    }
  }

  /**
   * Tek seferlik schedule kontrolü
   */
  private checkOnceSchedule(schedule: Schedule, now: Date): boolean {
    const startDate = new Date(schedule.startDate);
    const endDate = new Date(schedule.endDate);
    return now >= startDate && now <= endDate;
  }

  /**
   * Günlük tekrarlı schedule kontrolü
   * Gece yarısını geçen schedule'ları da destekler (örn: 21:00 - 08:00)
   */
  private checkDailySchedule(schedule: Schedule, now: Date): boolean {
    // Saat kontrolü yap - UYARI: Local time kullan, UTC'ye çevirme!
    const startTime = this.extractTime(schedule.startDate);
    const endTime = this.extractTime(schedule.endDate);
    const currentTime = this.extractTime(now); // ← UTC bug düzeltildi, toISOString() kaldırıldı

    // Gece yarısını geçiyor mu? (örn: 21:00 - 08:00)
    if (endTime < startTime) {
      return currentTime >= startTime || currentTime < endTime;
    }

    // Normal aralık (örn: 09:00 - 17:00)
    return currentTime >= startTime && currentTime <= endTime;
  }

  /**
   * Haftalık tekrarlı schedule kontrolü
   */
  private checkWeeklySchedule(schedule: Schedule, now: Date): boolean {
    // Gün kontrolü
    const recurrence = schedule.recurrence || {};
    const daysOfWeek = recurrence.daysOfWeek || [];
    const currentDay = now.getDay(); // 0 = Pazar, 1 = Pazartesi, ...

    if (daysOfWeek.length > 0 && !daysOfWeek.includes(currentDay)) {
      return false;
    }

    // Saat kontrolü
    return this.checkDailySchedule(schedule, now);
  }

  /**
   * Aylık tekrarlı schedule kontrolü
   */
  private checkMonthlySchedule(schedule: Schedule, now: Date): boolean {
    // Gün kontrolü
    const recurrence = schedule.recurrence || {};
    const daysOfMonth = recurrence.daysOfMonth || [];
    const currentDayOfMonth = now.getDate();

    if (daysOfMonth.length > 0 && !daysOfMonth.includes(currentDayOfMonth)) {
      return false;
    }

    // Saat kontrolü
    return this.checkDailySchedule(schedule, now);
  }

  /**
   * Tarihten saat bilgisini dakika cinsinden çıkarır
   */
  private extractTime(date: string | Date): number {
    const d = new Date(date);
    return d.getHours() * 60 + d.getMinutes();
  }

  private getCurrentPlaylistFromStorage(): Playlist | null {
    return localStateStore.getJson<Playlist>('currentPlaylist', null);
  }

  private getScheduleId(schedule?: Schedule | null): string | null {
    return schedule?._id || schedule?.id || null;
  }

  private getScheduledPlaylistId(schedule?: Schedule | null): string | null {
    const originalPlaylistId = schedule?.playlist?._id || (schedule?.playlist as any)?.id;
    return originalPlaylistId ? `scheduled_${originalPlaylistId}` : null;
  }

  private findActiveScheduleUsingPlaylist(scheduledPlaylistId: string | null, excludeScheduleId?: string | null): Schedule | null {
    if (!scheduledPlaylistId) {
      return null;
    }

    const excludedId = excludeScheduleId ? String(excludeScheduleId) : null;
    const now = new Date();

    if (this.activeSchedule) {
      const activeId = this.getScheduleId(this.activeSchedule);

      if (
        activeId &&
        String(activeId) !== excludedId &&
        this.getScheduledPlaylistId(this.activeSchedule) === scheduledPlaylistId &&
        this.isScheduleActive(this.activeSchedule, now)
      ) {
        return this.activeSchedule;
      }
    }

    for (const schedule of this.schedules.values()) {
      const scheduleId = this.getScheduleId(schedule);

      if (scheduleId && String(scheduleId) === excludedId) {
        continue;
      }

      if (
        this.getScheduledPlaylistId(schedule) === scheduledPlaylistId &&
        this.isScheduleActive(schedule, now)
      ) {
        return schedule;
      }
    }

    return null;
  }

  /**
   * Schedule playlist'ini aktif eder
   * ÖNEMLİ: Manuel playlist SİLİNMEZ - schedule playlist ayrı tutulur
   *
   * Desktop'tan FARKLAR:
   * - İndirme yok, CDN URL'leri direkt kullanılır
   * - Event ile Player.tsx'e bildirim yapılır
   */
  private async activateSchedulePlaylist(schedule: Schedule): Promise<void> {
    try {
      const playlist = schedule.playlist;
      if (!playlist) {
        console.error('[ScheduleService] Schedule\'da playlist bulunamadı!');
        return;
      }

      const originalPlaylistId = playlist._id || (playlist as any).id;
      if (!originalPlaylistId) {
        console.error('[ScheduleService] Playlist ID bulunamadı!');
        return;
      }

      // Fallback playlist'i sadece YOKSA kaydet
      // Schedule update sırasında fallback DEĞİŞMEMELİ - sadece ilk schedule'da kaydedilir
      const existingFallback = localStateStore.getString('fallbackPlaylist');

      if (!existingFallback) {
        // Fallback yok - mevcut currentPlaylist'i fallback olarak sakla (eğer scheduled değilse)
        const current = localStateStore.getString('currentPlaylist');
        if (current) {
          try {
            const currentParsed = JSON.parse(current);
            // SADECE normal (scheduled olmayan) playlist'i fallback yap
            if (!currentParsed._id?.startsWith('scheduled_')) {
              localStateStore.setString('fallbackPlaylist', current);
              console.log('[ScheduleService] Fallback playlist kaydedildi:', currentParsed.name);
            }
          } catch (e) {
            // Parse hatası - kaydetme
            console.error('[ScheduleService] Fallback kaydetme hatası:', e);
          }
        }
      } else {
        console.log('[ScheduleService] Fallback zaten mevcut, değiştirilmedi');
      }

      // Schedule playlist'i "scheduled_" prefix ile kaydet
      const scheduledPlaylistId = `scheduled_${originalPlaylistId}`;

      // Şarkıları normalize et (id -> _id)
      const normalizedSongs = (playlist.songs || []).map(song => ({
        ...song,
        _id: song._id || (song as any).id,
        cdnUrl: song.cdnUrl || (song as any).url,
        filePath: song.filePath || (song as any).path
      }));

      // Playlist verisini hazırla - scheduled_ prefix ile
      const playlistData: Playlist = {
        _id: scheduledPlaylistId,
        originalId: originalPlaylistId,
        name: playlist.name,
        artwork: playlist.artwork || (playlist as any).artworkUrl,
        songs: normalizedSongs,
        isScheduled: true,
        scheduleId: schedule._id || schedule.id
      };

      // Scheduled playlist'i currentPlaylist'e yaz
      localStateStore.setJson('currentPlaylist', playlistData);

      // Player.tsx'e bildirim gönder
      window.dispatchEvent(new CustomEvent('schedule-playlist-activated', {
        detail: {
          scheduleId: schedule._id || schedule.id,
          playlistId: scheduledPlaylistId,
          playlist: playlistData
        }
      }));
    } catch (error) {
      console.error('[ScheduleService] Schedule playlist aktifleştirme hatası:', error);
    }
  }

  /**
   * Manuel playlist'e geri döner
   * Desktop'taki switchToManualPlaylist() metodunun web adaptasyonu
   */
  private async switchToManualPlaylist(): Promise<void> {
    try {
      // Fallback playlist'i currentPlaylist'e geri yaz
      const fallbackPlaylist = localStateStore.getString('fallbackPlaylist');
      const currentScheduledPlaylist = this.getCurrentPlaylistFromStorage();

      if (!fallbackPlaylist && currentScheduledPlaylist?._id?.startsWith('scheduled_')) {
        console.warn('[ScheduleService] Fallback playlist yok; muzik kesilmesin diye son schedule playlist devam ediyor');
        return;
      }

      if (fallbackPlaylist) {
        localStateStore.setString('currentPlaylist', fallbackPlaylist);
        localStateStore.remove('fallbackPlaylist');

        const playlist = JSON.parse(fallbackPlaylist);
        window.dispatchEvent(new CustomEvent('switch-to-manual-playlist', {
          detail: {
            playlistId: playlist._id,
            playlist: playlist
          }
        }));
      } else {
        console.warn('[ScheduleService] Fallback playlist bulunamadı!');
      }
    } catch (error) {
      console.error('[ScheduleService] Manuel playlist\'e geçiş hatası:', error);
    }
  }

  /**
   * Schedule'ı kaydeder ama checkSchedules çağırmaz
   * Toplu yüklemelerde kullanılır (activePlaylist mesajından gelen allSchedules)
   */
  saveScheduleWithoutCheck(scheduleData: Schedule): void {
    try {
      if (!scheduleData) return;

      const scheduleId = scheduleData._id || scheduleData.id;
      if (!scheduleId) return;

      // Schedule'ı Map'e kaydet
      this.schedules.set(scheduleId, scheduleData);

      // localStorage'a kaydet
      this.saveSchedulesToStorage();

      console.log(`[ScheduleService] Schedule kaydedildi (check yok): ${scheduleId}`);
    } catch (error) {
      console.error('[ScheduleService] Schedule kaydetme hatası:', error);
    }
  }

  /**
   * Backend auth/activePlaylist sync'i tum listeyi temsil eder.
   * Bos liste geldiyse lokalde eski schedule kalmamali.
   */
  async replaceSchedulesFromBackend(scheduleList: Schedule[]): Promise<void> {
    try {
      const previousActiveId = this.activeSchedule ? (this.activeSchedule._id || this.activeSchedule.id) : null;
      this.schedules.clear();

      for (const schedule of scheduleList || []) {
        const scheduleId = schedule?._id || schedule?.id;
        if (scheduleId) {
          this.schedules.set(scheduleId, schedule);
        }
      }

      const now = new Date();
      const activeSchedule = Array.from(this.schedules.values()).find(schedule =>
        this.isScheduleActive(schedule, now)
      ) || null;

      this.activeSchedule = activeSchedule;
      this.saveSchedulesToStorage();

      if (activeSchedule) {
        await this.activateSchedulePlaylist(activeSchedule);
        return;
      }

      const currentPlaylist = this.getCurrentPlaylistFromStorage();
      const currentIsScheduled = currentPlaylist?._id?.startsWith('scheduled_') === true;

      if (previousActiveId || currentIsScheduled) {
        await this.switchToManualPlaylist();
      }
    } catch (error) {
      console.error('[ScheduleService] Backend schedule sync hatasÄ±:', error);
    }
  }

  /**
   * Aktif schedule bilgisini döndürür
   */
  getActiveSchedule(): Schedule | null {
    return this.activeSchedule;
  }

  hasActiveSchedule(scheduleList: Schedule[]): boolean {
    const now = new Date();
    return (scheduleList || []).some(schedule => this.isScheduleActive(schedule, now));
  }

  /**
   * Tüm schedule'ları döndürür
   */
  getAllSchedules(): Schedule[] {
    return Array.from(this.schedules.values());
  }

  /**
   * Servis temizliği
   */
  cleanup(): void {
    this.stopPeriodicCheck();
    this.schedules.clear();
    this.activeSchedule = null;
  }
}

// Singleton export
export default ScheduleService.getInstance();
